import { defineStore } from 'pinia'
import { ref } from 'vue'
import { searchCoverage } from '../../services/api/fhirAPI/coverage/coverage.js'
import { useSelectedPlanStore } from '@/stores/plans/selectedPlan.ts'

const coverageSpan: number = 36

function xMonthsFromToday(months: number = coverageSpan) {
	const today = new Date()
	const dateMax = new Date(today)
	const newDate = new Date(dateMax.setMonth(dateMax.getMonth() - months))

	return newDate
}

function withinXMonths(val: string, months: number = coverageSpan) {
	const today = new Date()
	const dateMax = new Date(today)
	dateMax.setMonth(dateMax.getMonth() - months)
	const dateToCheck = new Date(val)
	return dateMax <= dateToCheck
}

export const useCoverageStore = defineStore(
	'coverageStore',
	() => {
		// ====---------------====
		// State
		// ====---------------====
		const planStore = useSelectedPlanStore().getStore()
		const coverage = ref<ICoverageState>({})
		const loaded = ref(false)

		// ====---------------====
		// Getters
		// ====---------------====
		function getGroupId(memberId: string) {
			const arrClass = coverage.value[memberId][0].class
			let code = ''
			arrClass?.forEach((element: IFhirResource) => {
				if (element?.type?.text?.toLowerCase() == 'group') {
					code = element.type.id
				}
			})
			return code == '' ? false : code
		}

		// Gets the most recent coverage for each member
		// This is used for most calls (group IDs/coverage and copays)
		function getRecentCoverage(): ICoverageState {
			const mostRecent = {}

			for (const memberId in coverage.value) {
				mostRecent[memberId] = coverage.value[memberId][0]
			}

			return mostRecent
		}

		// Gets the current and previous plans years
		// Used for Spending details
		function getCurrentPreviousCoverage(dateRange: string) {
			const contracts: Array<object> = []

			for (const member in coverage.value) {
				const planList = coverage.value[member]
				planList.forEach((plan: IFhirResource) => {
					const rangeDate = new Date(dateRange)
					const planDate = plan.period?.end ? new Date(plan.period?.end) : null

					if (planDate && planDate >= rangeDate) contracts.push(plan)
				})
			}

			return contracts
		}

		function getRxTiers() {
			const copayTiers =
				coverage.value[planStore.memberId][0]?.costToBeneficiary

					?.filter((x: IFhirCost) => {
						return (
							x.type.coding[0]?.code?.includes('rx_tier') &&
							(x.type.coding[0].code.includes('copay') || x.type.coding[0].code.includes('coins'))
						)
					})
					.map((x: IFhirCost) => {
						if (x.type.coding[0].code?.includes('copay')) {
							return {
								label: x.type.text.replace('RX ', '').replace('Copay', ''),
								amount: `$${x?.valueMoney.value?.toFixed(2)}`
							}
						} else if (x.type.coding[0].code?.includes('coins')) {
							return {
								label: x.type.text.replace('RX ', '').replace('Coinsurance', ''),
								amount: `${x?.valueQuantity?.value}% coinsurance`
							}
						}
					}) ?? []
			return copayTiers.length > 0 ? copayTiers : false
		}

		// ====---------------====
		// Actions
		// ====---------------====
		function updateCoverage(memberId: string, payload: object) {
			if (coverage.value[memberId]) {
				coverage.value[memberId].push(payload)
			} else {
				coverage.value[memberId] = []
				coverage.value[memberId].push(payload)
			}
			loaded.value = true
		}

		let loadCoverageRequest: Promise<string> | null = null
		const loadCoverageStore = async () => {
			if (loadCoverageRequest) {
				return loadCoverageRequest
			}
			loadCoverageRequest = new Promise(async (resolve, reject) => {
				try {
					let apiPlanId = planStore.isPolicyHolder ? planStore.contractId : planStore.memberId
					await searchCoverage(
						apiPlanId,
						`&versionId=2&_count=50&period=ge${xMonthsFromToday().getFullYear()}-01-01`
					)
						.then((res) => {
							if (res.total === 0) return
							res.loaded = true
							res.entry.forEach((covItem) => {
								const covItemType = covItem.resource.type.coding[0].code.toLowerCase()
								// check if active AND not already in store
								if (
									covItem.resource.status === 'active' ||
									(covItem.resource.status === 'cancelled' &&
										withinXMonths(covItem.resource.period.end, coverageSpan))
								) {
									// HIP
									if (covItemType === 'hip' && covItem.resource.id.includes(planStore.contractId)) {
										updateCoverage(covItem.resource.id, covItem.resource)
									}
									// Dental
									else if (
										covItemType === 'dental' &&
										covItemType === planStore?.contractType?.toLowerCase() &&
										covItem.resource.id.includes(planStore?.contractId)
									) {
										updateCoverage(covItem.resource.id, covItem.resource)
									}
									// Vision
									else if (
										covItemType === 'vision' &&
										covItemType === planStore?.contractType?.toLowerCase() &&
										covItem.resource.id.includes(planStore.contractId)
									) {
										updateCoverage(covItem.resource.id, covItem.resource)
									}
								}
							})
						})
						.catch((err: string) => {
							console.error(err)
						})

					resolve('')
				} catch (err) {
					reject(err)
				} finally {
					loadCoverageRequest = null
				}
			})
			return loadCoverageRequest
		}

		function $reset() {
			coverage.value = {}
			loaded.value = false
		}

		return {
			coverage,
			loaded,
			getGroupId,
			getRecentCoverage,
			getCurrentPreviousCoverage,
			getRxTiers,
			loadCoverageStore,
			$reset
		}
	},
	{
		persist: {
			storage: sessionStorage
		}
	}
)
